import React, { useCallback, useEffect, useState } from "react";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import { Wallet, Copy, Check, Power, AlertTriangle, RefreshCw, TestTube2, Save, Info } from "lucide-react";
import { NETWORKS, useStickyNetwork, DEFAULT_LOCALNET_RPC } from "./WalletProvider";

const shortAddr = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");

function LocalnetRpcEditor() {
  const { localnetRpc, setLocalnetRpc } = useStickyNetwork();
  const [value, setValue] = useState(localnetRpc || DEFAULT_LOCALNET_RPC);
  const [status, setStatus] = useState(null); // {type:'ok'|'err', msg:string}
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => { setValue(localnetRpc || DEFAULT_LOCALNET_RPC); }, [localnetRpc]);

  const onSave = () => {
    const res = setLocalnetRpc(value);
    if (res.ok) {
      setStatus({ type: "ok", msg: "Saved. Reconnecting to Localnet RPC…" });
      setTimeout(() => setStatus(null), 2400);
    } else {
      setStatus({ type: "err", msg: res.error || "Invalid URL" });
    }
  };

  const isTunnel = /^https:\/\//i.test(value);

  return (
    <div className="sticky-lp-localrpc" data-testid="lp-localnet-rpc">
      <label className="sticky-lp-localrpc-label" htmlFor="lp-localnet-url">
        Localnet Custom RPC URL
      </label>
      <div className="sticky-lp-localrpc-row">
        <input
          id="lp-localnet-url"
          type="url"
          className="sticky-lp-localrpc-input"
          placeholder="https://xxxx.ngrok-free.dev"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          spellCheck={false}
          autoComplete="off"
          data-testid="lp-localnet-rpc-input"
        />
        <button
          type="button"
          className="sticky-lp-btn is-primary sticky-lp-localrpc-save"
          onClick={onSave}
          data-testid="lp-localnet-rpc-save"
        >
          <Save size={12} /> Save
        </button>
      </div>
      {status && (
        <p className={`sticky-lp-localrpc-status ${status.type === "ok" ? "is-ok" : "is-err"}`}>
          {status.msg}
        </p>
      )}
      {!isTunnel && (
        <p className="sticky-lp-localrpc-status is-warn">
          <AlertTriangle size={11} /> The browser will block plain <code>http://localhost</code> from this HTTPS page. Use an HTTPS tunnel (ngrok) URL.
        </p>
      )}
      <button
        type="button"
        className="sticky-lp-localrpc-help-toggle"
        onClick={() => setShowHelp((v) => !v)}
        data-testid="lp-localnet-rpc-help-toggle"
      >
        <Info size={11} /> {showHelp ? "Hide setup instructions" : "How to expose my local validator"}
      </button>
      {showHelp && (
        <div className="sticky-lp-localrpc-help" data-testid="lp-localnet-rpc-help">
          <p><b>1.</b> Start your Solana validator (WSL):</p>
          <pre><code>solana-test-validator</code></pre>
          <p><b>2.</b> In a second terminal, expose it via ngrok:</p>
          <pre><code>ngrok http 8899</code></pre>
          <p><b>3.</b> Copy the HTTPS forwarding URL (e.g. <code>https://abc.ngrok-free.dev</code>) and paste it above → <b>Save</b>.</p>
          <p><b>4.</b> In Phantom → <i>Settings → Developer Settings → Solana Custom RPC URL</i>, paste the same URL. Set network to <b>Localnet</b>.</p>
          <p className="sticky-lp-localrpc-help-note">
            The same URL must be used in both places — the browser and Phantom talk to the same tunnel, so signatures and balances stay in sync.
          </p>
        </div>
      )}
    </div>
  );
}

function NetworkSwitcher() {
  const { network, setNetworkId } = useStickyNetwork();
  return (
    <>
      <div className="sticky-lp-netswitch" role="tablist" aria-label="Solana network" data-testid="lp-network-switch">
        {Object.values(NETWORKS).map((n) => (
          <button
            key={n.id}
            type="button"
            role="tab"
            disabled={n.disabled}
            aria-selected={network.id === n.id}
            className={`sticky-lp-netbtn ${n.dotClass} ${network.id === n.id ? "is-active" : ""} ${n.disabled ? "is-disabled" : ""}`}
            onClick={() => !n.disabled && setNetworkId(n.id)}
            title={n.disabled ? "Mainnet is disabled in this phase" : n.label}
            data-testid={`lp-net-${n.id.toLowerCase()}`}
          >
            <span className="sticky-lp-netdot" />
            {n.label}
            {n.disabled && <span className="sticky-lp-lock">🔒</span>}
          </button>
        ))}
      </div>
      {network.id === "LOCALNET" && <LocalnetRpcEditor />}
    </>
  );
}

export function WalletPanel() {
  const { connection } = useConnection();
  const { publicKey, wallet, connecting, connected, disconnect } = useWallet();
  const { setVisible } = useWalletModal();
  const { network } = useStickyNetwork();

  const [balanceSol, setBalanceSol] = useState(null);
  const [balanceError, setBalanceError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchBalance = useCallback(async () => {
    if (!publicKey || !connection) return;
    setRefreshing(true);
    setBalanceError(null);
    try {
      const lamports = await connection.getBalance(publicKey, { commitment: "confirmed" });
      setBalanceSol(lamports / LAMPORTS_PER_SOL);
    } catch (e) {
      setBalanceSol(null);
      setBalanceError("Balance unavailable. Check your RPC connection and try again.");
    } finally {
      setRefreshing(false);
    }
  }, [publicKey, connection]);

  useEffect(() => {
    if (connected && publicKey) {
      fetchBalance();
      const id = setInterval(fetchBalance, 30_000); // read-only refresh, no tx
      return () => clearInterval(id);
    }
    setBalanceSol(null);
    setBalanceError(null);
    return undefined;
  }, [connected, publicKey, fetchBalance]);

  const onCopy = async () => {
    if (!publicKey) return;
    try {
      await navigator.clipboard.writeText(publicKey.toBase58());
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore */ }
  };

  const onDisconnect = async () => {
    try { await disconnect(); } catch { /* ignore */ }
  };

  return (
    <div className="sticky-lp-wallet" data-testid="lp-wallet-panel">
      <div className="sticky-lp-wallet-head">
        <div className="sticky-lp-wallet-title">
          <Wallet size={14} strokeWidth={2.4} /> Wallet
        </div>
        <span className={`sticky-lp-wallet-env ${network.dotClass}`} data-testid="lp-wallet-env">
          <TestTube2 size={12} /> {network.shortLabel}
        </span>
      </div>

      <NetworkSwitcher />

      {network.id === "MAINNET" && (
        <p className="sticky-lp-wallet-warn" role="alert">
          <AlertTriangle size={13} /> Mainnet is disabled in the current phase.
        </p>
      )}

      {!connected ? (
        <>
          <p className="sticky-lp-wallet-hint">
            Wallet connection is temporarily disabled while the STICKY Launchpad is in preview.
          </p>
          <button
            type="button"
            className="sticky-lp-btn is-primary sticky-lp-wallet-connect"
            onClick={() => {}}
            disabled
            aria-disabled="true"
            title="Wallet connection is temporarily disabled"
            data-testid="lp-wallet-connect"
          >
            <Wallet size={14} strokeWidth={2.5} />
            Connect Wallet (Coming Soon)
          </button>
          <p className="sticky-lp-note sticky-lp-note-center">
            Supported at launch: Phantom · Solflare · any Solana Wallet Standard extension
          </p>
        </>
      ) : (
        <>
          <div className="sticky-lp-wallet-status">
            <span className="sticky-lp-wallet-dot" aria-hidden="true" />
            Connected
            {wallet?.adapter?.name && <span className="sticky-lp-wallet-adapter"> · {wallet.adapter.name}</span>}
          </div>

          <div className="sticky-lp-wallet-addr" data-testid="lp-wallet-address">
            <code>{shortAddr(publicKey?.toBase58())}</code>
            <button
              type="button"
              className={`sticky-lp-ca-copy ${copied ? "is-copied" : ""}`}
              onClick={onCopy}
              data-testid="lp-wallet-copy"
            >
              {copied ? <Check size={11} /> : <Copy size={11} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="sticky-lp-wallet-bal" data-testid="lp-wallet-balance">
            <div className="sticky-lp-wallet-bal-label">{network.label} SOL</div>
            <div className="sticky-lp-wallet-bal-value">
              {balanceSol == null ? (balanceError ? "—" : "…") : `${balanceSol.toFixed(4)} SOL`}
            </div>
            <button
              type="button"
              className="sticky-lp-wallet-refresh"
              onClick={fetchBalance}
              disabled={refreshing}
              aria-label="Refresh balance"
              data-testid="lp-wallet-refresh"
            >
              <RefreshCw size={12} className={refreshing ? "is-spinning" : ""} />
            </button>
          </div>

          {balanceError && (
            <p className="sticky-lp-wallet-warn" role="alert">
              <AlertTriangle size={13} /> {balanceError}
            </p>
          )}

          <button
            type="button"
            className="sticky-lp-btn is-ghost sticky-lp-wallet-disconnect"
            onClick={onDisconnect}
            data-testid="lp-wallet-disconnect"
          >
            <Power size={13} /> Disconnect
          </button>
          <p className="sticky-lp-note sticky-lp-note-center">
            Signatures happen in your wallet · non-custodial
          </p>
        </>
      )}
    </div>
  );
}
